import type {CSSProperties} from '@mui/material';
import type {TextProps} from '@radix-ui/themes';
import {Text as RadixText} from '@radix-ui/themes';
import {clsx} from 'clsx';
import type {PropsWithChildren} from 'react';

import {Color, colorVarMap, useClasses} from '#theme';
import {is} from '#utils/predicates.js';

import * as styles from './style.module.scss';

type AllowedProps = Pick<TextProps, 'as' | 'className'>;

type Props = AllowedProps & {
  color?: Color;
  id?: string;
};

const generateColorMap = (color: Props['color']) => {
  return Object.entries(Color).reduce(
    (acc, [key, value]) => {
      const styleKey = `text${key}` as keyof typeof styles;

      if (styleKey in styles) {
        acc[styles[styleKey] as Color] = is(color, value);
      }

      return acc;
    },
    {} as Record<string, boolean>
  );
};

export const Text = ({
  className,
  color = 'body',
  ...props
}: PropsWithChildren<Props>) => {
  const classNames = useClasses({
    [styles.text]: true,
    ...generateColorMap(color),
  });

  return (
    <RadixText
      {...props}
      style={{'--color': colorVarMap[color]} as CSSProperties}
      className={clsx(classNames, className)}
    >
      {props.children}
    </RadixText>
  );
};
Text.displayName = 'Text';
